import React, { useState, useEffect } from 'react';
import {
  Container,
  Grid,
  Paper,
  Typography,
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Chip,
  Alert,
  CircularProgress,
  Fab,
  Card,
  CardContent,
  RadioGroup,
  FormControlLabel,
  Radio,
  Pagination,
  Stack,
  Divider
} from '@mui/material';
import {
  Add,
  Edit,
  Delete,
  Save,
  Cancel,
  Search,
  CloudUpload
} from '@mui/icons-material';
import axios from 'axios';
import BulkMCQImport from './BulkMCQImport';

const MCQManager = () => {
  const [mcqs, setMcqs] = useState([]);
  const [tests, setTests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [openDialog, setOpenDialog] = useState(false);
  const [openBulkImport, setOpenBulkImport] = useState(false);
  const [editingMcq, setEditingMcq] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterCategory, setFilterCategory] = useState('');
  const [filterDifficulty, setFilterDifficulty] = useState('');

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalPages, setTotalPages] = useState(0);
  const [totalMCQs, setTotalMCQs] = useState(0);

  const [formData, setFormData] = useState({
    question: '',
    options: [
      { text: '', imageUrl: '', width: '', height: '' },
      { text: '', imageUrl: '', width: '', height: '' },
      { text: '', imageUrl: '', width: '', height: '' }
    ],
    correctAnswer: 0,
    category: '',
    difficulty: 'medium',
    explanation: '',
    testId: ''
  });
  const [showImageInputs, setShowImageInputs] = useState({});

  useEffect(() => {
    fetchMCQs();
    fetchTests();
  }, []);

  // Fetch MCQs when pagination or filters change
  useEffect(() => {
    fetchMCQs();
  }, [currentPage, pageSize, searchTerm, filterDifficulty]);

  const fetchMCQs = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams({
        page: currentPage.toString(),
        limit: pageSize.toString()
      });

      if (searchTerm.trim()) {
        params.append('search', searchTerm.trim());
      }
      if (filterDifficulty) {
        params.append('difficulty', filterDifficulty);
      }

      const response = await axios.get(`/api/mcqs?${params}`);
      setMcqs(response.data.mcqs || []);

      // Update pagination info
      if (response.data.pagination) {
        setTotalPages(response.data.pagination.totalPages);
        setTotalMCQs(response.data.pagination.total);
      }
    } catch (error) {
      setError('Failed to load MCQs');
      console.error('MCQ fetch error:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchTests = async () => {
    try {
      const res = await axios.get('/api/tests');
      setTests(res.data.tests || []);
    } catch (e) {
      // Non-fatal for MCQ manager
    }
  };

  const handleOpenDialog = (mcq = null) => {
    if (mcq) {
      setEditingMcq(mcq);
      setFormData({
        question: mcq.question,
        options: [...mcq.options],
        correctAnswer: mcq.correctAnswer,
        category: mcq.category,
        difficulty: mcq.difficulty,
        explanation: mcq.explanation || '',
        questionImageUrl: mcq.questionImageUrl || '',
        questionImageWidth: mcq.questionImageWidth || '',
        questionImageHeight: mcq.questionImageHeight || ''
      });
    } else {
      setEditingMcq(null);
      setFormData({
        question: '',
        options: [
          { text: '', imageUrl: '', width: '', height: '' },
          { text: '', imageUrl: '', width: '', height: '' },
          { text: '', imageUrl: '', width: '', height: '' }
        ],
        correctAnswer: 0,
        category: '',
        difficulty: 'medium',
        explanation: '',
        testId: ''
      });
      setShowImageInputs({});
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingMcq(null);
    setError('');
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleOptionChange = (index, field, value) => {
    const newOptions = [...formData.options];
    const current = newOptions[index] || { text: '', imageUrl: '', width: '', height: '' };
    newOptions[index] = { ...current, [field]: value };
    setFormData(prev => ({ ...prev, options: newOptions }));
  };

  const validateForm = () => {
    if (!formData.question.trim()) {
      setError('Question is required');
      return false;
    }
    // Require test selection
    if (!formData.testId) {
      setError('Please select a test to assign this MCQ to');
      return false;
    }
    if (formData.options.length < 3) {
      setError('Please provide at least 3 options');
      return false;
    }
    const invalid = formData.options.some(opt => !((opt.text || '').trim() || (opt.imageUrl || '').trim()));
    if (invalid) {
      setError('Each option must have text or an image URL');
      return false;
    }
    if (formData.correctAnswer < 0 || formData.correctAnswer >= formData.options.length) {
      setError('Please select a correct answer');
      return false;
    }
    return true;
  };

  const handleSubmit = async () => {
    if (!validateForm()) {
      return;
    }

    setLoading(true);
    try {
      if (editingMcq) {
        await axios.put(`/api/mcqs/${editingMcq.id}`, formData);
      } else {
        await axios.post('/api/mcqs', formData);
      }

      // Reset to first page and fetch MCQs to show newly added MCQ
      setCurrentPage(1);
      await fetchMCQs();
      handleCloseDialog();
    } catch (error) {
      setError(error.response?.data?.error || 'Failed to save MCQ');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (mcqId) => {
    if (!window.confirm('Are you sure you want to delete this MCQ?')) {
      return;
    }

    try {
      await axios.delete(`/api/mcqs/${mcqId}`);
      // If current page becomes empty after deletion, go to previous page
      if (mcqs.length === 1 && currentPage > 1) {
        setCurrentPage(currentPage - 1);
      } else {
        await fetchMCQs();
      }
    } catch (error) {
      setError('Failed to delete MCQ');
    }
  };

  const handleBulkImportComplete = (results) => {
    if (results.successful > 0) {
      setError(`Successfully imported ${results.successful} MCQs!`);
      // Reset to first page to show newly imported MCQs
      setCurrentPage(1);
      fetchMCQs(); // Refresh the MCQ list
    }
    if (results.failed > 0) {
      setError(`${results.successful} MCQs imported successfully, but ${results.failed} failed. Check console for details.`);
      console.log('Failed MCQs:', results.failedMCQs);
    }
  };

  // Handle pagination changes
  const handlePageChange = (event, newPage) => {
    setCurrentPage(newPage);
  };

  const handlePageSizeChange = (event) => {
    setPageSize(parseInt(event.target.value));
    setCurrentPage(1); // Reset to first page when changing page size
  };

  // Handle search with debouncing
  const handleSearchChange = (value) => {
    setSearchTerm(value);
    setCurrentPage(1); // Reset to first page when searching
  };

  const handleDifficultyFilterChange = (value) => {
    setFilterDifficulty(value);
    setCurrentPage(1); // Reset to first page when filtering
  };

  // Categories removed

  const getDifficultyColor = (difficulty) => {
    switch (difficulty) {
      case 'easy': return 'success';
      case 'medium': return 'warning';
      case 'hard': return 'error';
      default: return 'default';
    }
  };


  return (
    <Container maxWidth="lg">
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" component="h1" gutterBottom>
          MCQ Manager
        </Typography>
        <Typography variant="subtitle1" color="text.secondary">
          Create, edit, and manage multiple choice questions for your tests.
        </Typography>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {/* Filters and Search */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              label="Search MCQs"
              variant="outlined"
              value={searchTerm}
              onChange={(e) => handleSearchChange(e.target.value)}
              InputProps={{
                startAdornment: <Search sx={{ mr: 1, color: 'text.secondary' }} />
              }}
            />
          </Grid>
          {/* Category filter removed */}
          <Grid item xs={12} md={3}>
            <FormControl fullWidth>
              <InputLabel>Filter by Difficulty</InputLabel>
              <Select
                value={filterDifficulty}
                label="Filter by Difficulty"
                onChange={(e) => handleDifficultyFilterChange(e.target.value)}
              >
                <MenuItem value="">All Difficulties</MenuItem>
                <MenuItem value="easy">Easy</MenuItem>
                <MenuItem value="medium">Medium</MenuItem>
                <MenuItem value="hard">Hard</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={3}>
            <Box display="flex" gap={1}>
              <Button
                variant="contained"
                startIcon={<Add />}
                onClick={() => handleOpenDialog()}
                sx={{ flexGrow: 1 }}
              >
                Add MCQ
              </Button>
              <Button
                variant="outlined"
                startIcon={<CloudUpload />}
                onClick={() => setOpenBulkImport(true)}
                sx={{ flexGrow: 1 }}
              >
                Bulk Import
              </Button>
            </Box>
          </Grid>
        </Grid>
      </Paper>

      {/* MCQ List */}
      <Paper sx={{ p: 3 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <Typography variant="h6">
            MCQ Questions ({totalMCQs} total)
          </Typography>
          <FormControl size="small" sx={{ minWidth: 120 }}>
            <InputLabel>Per Page</InputLabel>
            <Select
              value={pageSize}
              label="Per Page"
              onChange={handlePageSizeChange}
            >
              <MenuItem value={5}>5</MenuItem>
              <MenuItem value={10}>10</MenuItem>
              <MenuItem value={25}>25</MenuItem>
              <MenuItem value={50}>50</MenuItem>
            </Select>
          </FormControl>
        </Box>

        {loading ? (
          <Box display="flex" justifyContent="center" alignItems="center" height={200}>
            <CircularProgress />
          </Box>
        ) : mcqs.length > 0 ? (
          <>
            <List>
              {mcqs.map((mcq, index) => (
                <ListItem
                  key={mcq.id}
                  divider={index < mcqs.length - 1}
                  sx={{ py: 2 }}
                >
                  <ListItemText
                    primary={
                      <Box>
                        <Typography variant="subtitle1" gutterBottom>
                          {mcq.question}
                        </Typography>
                        <Box display="flex" gap={1} mb={1}>
                          <Chip
                            label={mcq.difficulty}
                            size="small"
                            color={getDifficultyColor(mcq.difficulty)}
                          />
                          {mcq.createdAt && (
                            <Chip
                              label={`Added: ${new Date(mcq.createdAt).toLocaleDateString()}`}
                              size="small"
                              variant="outlined"
                            />
                          )}
                        </Box>
                      </Box>
                    }
                    secondary={
                      <Box>
                        <Typography variant="body2" color="text.secondary">
                          Options: {mcq.options.map((opt) => (typeof opt === 'string' ? opt : (opt.text || '(image)'))).join(' | ')}
                        </Typography>
                        <Typography variant="body2" color="success.main">
                          Correct Answer: {(() => { const opt = mcq.options[mcq.correctAnswer]; return typeof opt === 'string' ? opt : (opt?.text || '(image)'); })()}
                        </Typography>
                      </Box>
                    }
                  />
                  <ListItemSecondaryAction>
                    <IconButton
                      edge="end"
                      aria-label="edit"
                      onClick={() => handleOpenDialog(mcq)}
                      sx={{ mr: 1 }}
                    >
                      <Edit />
                    </IconButton>
                    <IconButton
                      edge="end"
                      aria-label="delete"
                      onClick={() => handleDelete(mcq.id)}
                      color="error"
                    >
                      <Delete />
                    </IconButton>
                  </ListItemSecondaryAction>
                </ListItem>
              ))}
            </List>

            {/* Pagination Controls */}
            {totalPages > 1 && (
              <>
                <Divider sx={{ my: 2 }} />
                <Box display="flex" justifyContent="center" alignItems="center" gap={2}>
                  <Typography variant="body2" color="text.secondary">
                    Showing {((currentPage - 1) * pageSize) + 1}-{Math.min(currentPage * pageSize, totalMCQs)} of {totalMCQs}
                  </Typography>
                  <Pagination
                    count={totalPages}
                    page={currentPage}
                    onChange={handlePageChange}
                    color="primary"
                    showFirstButton
                    showLastButton
                  />
                </Box>
              </>
            )}
          </>
        ) : (
          <Box display="flex" justifyContent="center" alignItems="center" height={200}>
            <Typography color="text.secondary">
              {searchTerm || filterDifficulty ? 'No MCQs match your search criteria' : 'No MCQs created yet'}
            </Typography>
          </Box>
        )}
      </Paper>

      {/* Add/Edit MCQ Dialog */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          {editingMcq ? 'Edit MCQ' : 'Add New MCQ'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Question"
                  multiline
                  rows={3}
                  value={formData.question}
                  onChange={(e) => handleInputChange('question', e.target.value)}
                />
              </Grid>
              <Grid item xs={12}>
                <Typography variant="subtitle2" gutterBottom>Question Image (optional)</Typography>
                <Box display="flex" gap={2} alignItems="center">
                  <TextField
                    label="Image URL"
                    fullWidth
                    value={formData.questionImageUrl || ''}
                    onChange={(e) => handleInputChange('questionImageUrl', e.target.value)}
                  />
                  <TextField
                    label="Width (px)"
                    type="number"
                    value={formData.questionImageWidth || ''}
                    onChange={(e) => handleInputChange('questionImageWidth', e.target.value)}
                    sx={{ width: 150 }}
                  />
                  <TextField
                    label="Height (px)"
                    type="number"
                    value={formData.questionImageHeight || ''}
                    onChange={(e) => handleInputChange('questionImageHeight', e.target.value)}
                    sx={{ width: 160 }}
                  />
                </Box>
                {formData.questionImageUrl && (
                  <Box sx={{ mt: 1, border: '1px solid', borderColor: 'divider', p: 0.5, textAlign: 'center' }}>
                    <img
                      src={formData.questionImageUrl}
                      alt="Question"
                      style={{
                        maxWidth: '100%',
                        width: formData.questionImageWidth ? `${formData.questionImageWidth}px` : undefined,
                        height: formData.questionImageHeight ? `${formData.questionImageHeight}px` : 'auto'
                      }}
                    />
                  </Box>
                )}
              </Grid>

              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel>Assign to Test</InputLabel>
                  <Select
                    value={formData.testId}
                    label="Assign to Test"
                    onChange={(e) => handleInputChange('testId', e.target.value)}
                  >
                    {tests.map((t) => (
                      <MenuItem key={t.id} value={t.id}>{t.name}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel>Difficulty</InputLabel>
                  <Select
                    value={formData.difficulty}
                    label="Difficulty"
                    onChange={(e) => handleInputChange('difficulty', e.target.value)}
                  >
                    <MenuItem value="easy">Easy</MenuItem>
                    <MenuItem value="medium">Medium</MenuItem>
                    <MenuItem value="hard">Hard</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12}>
                <Typography variant="subtitle1" gutterBottom>
                  Options
                </Typography>
                <RadioGroup
                  value={formData.correctAnswer}
                  onChange={(e) => handleInputChange('correctAnswer', parseInt(e.target.value))}
                >
                  {formData.options.map((option, index) => (
                    <Box key={index} display="flex" alignItems="center" mb={1}>
                      <FormControlLabel
                        value={index}
                        control={<Radio />}
                        label=""
                        sx={{ mr: 1 }}
                      />
                      <TextField
                        fullWidth
                        label={`Option ${index + 1} Text`}
                        value={option.text}
                        onChange={(e) => handleOptionChange(index, 'text', e.target.value)}
                      />
                      {formData.options.length > 3 && (
                        <IconButton aria-label="delete option" color="error" onClick={() => {
                          const newOptions = formData.options.filter((_, i) => i !== index);
                          let newCorrect = formData.correctAnswer;
                          if (index === formData.correctAnswer) newCorrect = 0;
                          else if (index < formData.correctAnswer) newCorrect = formData.correctAnswer - 1;
                          handleInputChange('options', newOptions);
                          handleInputChange('correctAnswer', newCorrect);
                        }} sx={{ ml: 1 }}>
                          <Delete />
                        </IconButton>
                      )}
                      {/* Image per-option removed */}
                    </Box>
                  ))}
                </RadioGroup>
                {/* Per-option image editors removed */}
                <Box>
                  <Button
                    startIcon={<Add />}
                    onClick={() => handleInputChange('options', [...formData.options, { text: '', imageUrl: '', width: '', height: '' }])}
                    sx={{ mt: 1 }}
                  >
                    Add Option
                  </Button>
                </Box>
              </Grid>

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Explanation (Optional)"
                  multiline
                  rows={2}
                  value={formData.explanation}
                  onChange={(e) => handleInputChange('explanation', e.target.value)}
                  helperText="Provide an explanation for the correct answer"
                />
              </Grid>
            </Grid>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog} startIcon={<Cancel />}>
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            startIcon={<Save />}
            disabled={loading}
          >
            {loading ? <CircularProgress size={20} /> : (editingMcq ? 'Update' : 'Create')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Bulk Import Dialog */}
      <BulkMCQImport
        open={openBulkImport}
        onClose={() => setOpenBulkImport(false)}
        tests={tests}
        onImportComplete={handleBulkImportComplete}
      />
    </Container>
  );
};

export default MCQManager; 